


































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";

import { InvestmentFeeSchedule, FeeCollectionType } from "@/shared/types";
import { BankCustodian, InvestmentFeeScheduleSettingsFormVariant } from "@/admin/types";
import { useEmployerStore } from "@/admin/store/employer";
import { useNotifierStore } from "@/shared/store/notifier";
import InvestmentFeeScheduleSettings from "@/admin/components/shared/InvestmentFeeScheduleSettings.vue";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

@Component({
  components: {
    InvestmentFeeScheduleSettings,
  }
})
export default class EmployerInvestmentSettings extends Vue {
  @Prop() employerId: number;

  employerStore = useEmployerStore();
  notifierStore = useNotifierStore();
  custodianSettingsStore = useCustodianSettingsStore();
  investmentSubmitDisabled = false;
  isPartnerInvestmentSettingsActive: boolean = null;
  investmentFeeCollectionType: FeeCollectionType = null;
  investmentFeeSchedules: InvestmentFeeSchedule[] = null;

  created () {
    this.fetchInvestmentSettings();
  }

  mounted () {
    this.onFeeCollectionChange(this.investmentFeeCollectionForEmployer);
    this.onFeeSchedulesChange(this.investmentFeeSchedulesForEmployer);
    this.onPartnerInvestmentSettingsActiveChange(this.isPartnerInvestmentSettingsActiveForEmployer);
  }

  @Watch("investmentFeeCollectionForEmployer", { deep: true })
  onFeeCollectionChange (feeCollection) {
    this.investmentFeeCollectionType = feeCollection;
  }

  @Watch("investmentFeeSchedulesForEmployer", { deep: true })
  onFeeSchedulesChange (feeSchedules) {
    this.investmentFeeSchedules = feeSchedules;
  }

  @Watch("isPartnerInvestmentSettingsActiveForEmployer")
  onPartnerInvestmentSettingsActiveChange (flag) {
    this.isPartnerInvestmentSettingsActive = flag;
  }

  get investmentFeeCollectionForEmployer () {
    return this.employerStore.employerInvestmentFeeCollection(this.employerId);
  }

  get investmentFeeSchedulesForEmployer () {
    return this.employerStore.employerInvestmentFeeSchedules(this.employerId);
  }

  get isPartnerInvestmentSettingsActiveForEmployer () {
    return this.employerStore.isPartnerInvestmentSettingsActive(this.employerId);
  }

  get isLoaded () {
    return this.employerStore.isEmployerInvestmentFeeCollectionLoaded(this.employerId)
    && this.employerStore.isEmployerInvestmentFeeSchedulesLoaded(this.employerId)
    && this.custodianSettingsStore.isEmployerCustodianLoaded(this.employerId);
  }

  get feeCollectionOptions () {
    let options = [
      { value: FeeCollectionType.InvestmentAccount, text: "Participant Account" },
      { value: FeeCollectionType.Partner, text: "Partner" },
      { value: FeeCollectionType.Employer, text: "Employer" },
      { value: FeeCollectionType.HsaBank, text: "HSA Bank" }
    ];

    // HSA Bank cannot collect investment fees from HSA account
    if (this.custodianSettingsStore.employerCustodian !== BankCustodian.HsaBank) {
      options = [{ value: FeeCollectionType.HsaAccount, text: "Participant HSA Account" }, ...options];
    }

    return options;
  }

  @Watch("employerId")
  fetchInvestmentSettings () {
    this.investmentFeeCollectionType = null;
    this.investmentFeeSchedules = null;
    this.isPartnerInvestmentSettingsActive = null;
    this.investmentSubmitDisabled = false;
    this.employerStore.getEmployerInvestmentFeeCollectionType(this.employerId);
    this.employerStore.getEmployerInvestmentFeeSchedules(this.employerId);
    this.custodianSettingsStore.getEmployerCustodian(this.employerId);
  }

  updateEmployerInvestmentSettings () {
      if (this.investmentSubmitDisabled) {
        return;
      }

      this.$validator.validateAll().then(isValid => {

      if (!isValid) {
        return;
      }

      this.investmentSubmitDisabled = true;

      // remove active employer settings if partner override selected, else update employer settings
      if (this.isPartnerInvestmentSettingsActive) {
        return this.employerStore.deleteEmployerInvestmentFeeCollectionType(this.employerId)
          .then(() => this.employerStore.deleteEmployerInvestmentFeeSchedules(this.employerId))
          .then(() => this.notifierStore.setSuccess("Employer investment settings removed"))
          .catch(err => this.notifierStore.setDanger(err.message || "Error removing employer investment settings"));
      }
      else {
        return this.employerStore.updateEmployerInvestmentFeeSchedules(this.employerId, this.investmentFeeSchedules)
          .then(() => this.employerStore.updateEmployerInvestmentFeeCollectionType(
            this.employerId, this.investmentFeeCollectionType))
          .then(() => this.notifierStore.setSuccess("Employer investment settings saved"))
          .catch(err => this.notifierStore.setDanger(err.message || "Error saving employer investment settings"));
      }
    })
    .finally(() => this.investmentSubmitDisabled = false);
  }

  get investmentFeeScheduleSettingsFormVariant () {
    return InvestmentFeeScheduleSettingsFormVariant.employer;
  }
}
